/**
 * Utility to update channel cache without triggering full refetch
 * Updates the last-message-at timestamp for a specific channel and moves it to the top
 */
import { QueryClient } from '@tanstack/react-query';
import {
  ChannelsListResponse,
  ChannelListItemResponse,
} from '../types/backend';

export const updateChannelLastMessageAt = (
  queryClient: QueryClient,
  channelId: number,
  lastMessageAt: string
) => {
  // Update all channel list queries that might contain this channel
  queryClient.setQueriesData<{
    pages: ChannelsListResponse[];
    pageParams: any[];
  }>(
    {
      predicate: (query) => {
        const key = query.queryKey as any[];
        // Match queries like ['messaging', 'channels', { filters: {...} }]
        return (
          Array.isArray(key) &&
          key.length >= 2 &&
          key[0] === 'messaging' &&
          key[1] === 'channels'
        );
      },
    },
    (old) => {
      if (!old?.pages) return old;

      // Find the channel across all pages
      let updatedChannel: ChannelListItemResponse | null = null;
      let foundPageIndex = -1;
      let foundRowIndex = -1;

      for (let pageIdx = 0; pageIdx < old.pages.length; pageIdx++) {
        const rowIdx = old.pages[pageIdx].rows.findIndex(
          (ch) => String(ch.id) === String(channelId)
        );
        if (rowIdx !== -1) {
          updatedChannel = {
            ...old.pages[pageIdx].rows[rowIdx],
            'last-message-at': lastMessageAt,
          };
          foundPageIndex = pageIdx;
          foundRowIndex = rowIdx;
          break;
        }
      }

      if (!updatedChannel) return old; // Channel not found

      // Remove channel from its current position and insert at top of first page
      const newPages = old.pages.map((page, pageIdx) => {
        if (pageIdx === foundPageIndex) {
          // Remove from current page
          return {
            ...page,
            rows: page.rows.filter((_, idx) => idx !== foundRowIndex),
          };
        }
        return page;
      });

      // Insert updated channel at beginning of first page
      if (newPages.length > 0) {
        newPages[0] = {
          ...newPages[0],
          rows: [updatedChannel, ...newPages[0].rows],
        };
      }

      return {
        ...old,
        pages: newPages,
      };
    }
  );
};

/**
 * Utility to update channel metadata in cache without moving position
 * Useful for updating name, status, or other properties without triggering refetch
 */
export const updateChannelInCache = (
  queryClient: QueryClient,
  channelId: number,
  updates: Partial<ChannelListItemResponse>
) => {
  // Update all channel list queries that might contain this channel
  queryClient.setQueriesData<{
    pages: ChannelsListResponse[];
    pageParams: any[];
  }>(
    {
      predicate: (query) => {
        const key = query.queryKey as any[];
        // Match queries like ['messaging', 'channels', { filters: {...} }]
        return (
          Array.isArray(key) &&
          key.length >= 2 &&
          key[0] === 'messaging' &&
          key[1] === 'channels'
        );
      },
    },
    (old) => {
      if (!old?.pages) return old;

      // Find and update the channel in place (don't move it)
      const newPages = old.pages.map((page) => ({
        ...page,
        rows: page.rows.map((ch) => {
          if (String(ch.id) === String(channelId)) {
            return {
              ...ch,
              ...updates,
            };
          }
          return ch;
        }),
      }));

      return {
        ...old,
        pages: newPages,
      };
    }
  );
};

/**
 * Utility to add a newly created channel to the cache at the top
 * This ensures new channels appear at the top without needing a full refetch
 */
export const addNewChannelToCache = (
  queryClient: QueryClient,
  newChannel: ChannelListItemResponse
) => {
  // Update all channel list queries that should include this channel
  queryClient.setQueriesData<{
    pages: ChannelsListResponse[];
    pageParams: any[];
  }>(
    {
      predicate: (query) => {
        const key = query.queryKey as any[];
        // Match queries like ['messaging', 'channels', { filters: {...} }]
        if (
          !Array.isArray(key) ||
          key.length < 3 ||
          key[0] !== 'messaging' ||
          key[1] !== 'channels'
        ) {
          return false;
        }

        // Check if the filters match the new channel's type and section
        const filters = key[2]?.filters;
        if (!filters) return false;

        // IMPORTANT: Match channel type exactly (internal, client, dm)
        // Do NOT add if filter doesn't match - this prevents duplicates
        const typeMatches = filters.type === newChannel.type;

        // Match channel section (active, archived)
        // Default to 'active' if not specified
        const channelSection =
          newChannel.status === 'archived' ? 'archived' : 'active';
        const filterSection = filters.section || 'active';
        const sectionMatches = filterSection === channelSection;

        return typeMatches && sectionMatches;
      },
    },
    (old) => {
      if (!old?.pages || old.pages.length === 0) return old;

      // Remove any existing occurrences of this channel (normalize id compare)
      const cleanedPages = old.pages.map((page) => ({
        ...page,
        rows: page.rows.filter((ch) => String(ch.id) !== String(newChannel.id)),
      }));

      // Create updated channel with current timestamp to ensure it appears at top
      const channelWithTimestamp = {
        ...newChannel,
        'last-message-at':
          newChannel['last-message-at'] || new Date().toISOString(),
      };

      // Add channel to the top of the first page and update counts
      const newPages = cleanedPages.map((page, index) => {
        if (index === 0) {
          // Add to top of first page
          return {
            ...page,
            rows: [channelWithTimestamp, ...page.rows],
            count: page.count + 1, // Increment total count
          };
        }
        // Update count in all other pages to stay consistent
        return {
          ...page,
          count: page.count + 1,
        };
      });

      return {
        ...old,
        pages: newPages,
      };
    }
  );
};

/**
 * Utility to remove a channel from cache (e.g., when archived, user removed, etc.)
 * This removes the channel from all matching queries and updates counts
 */
export const removeChannelFromCache = (
  queryClient: QueryClient,
  channelId: number
) => {
  // Remove from all channel list queries
  queryClient.setQueriesData<{
    pages: ChannelsListResponse[];
    pageParams: any[];
  }>(
    {
      predicate: (query) => {
        const key = query.queryKey as any[];
        return (
          Array.isArray(key) &&
          key.length >= 2 &&
          key[0] === 'messaging' &&
          key[1] === 'channels'
        );
      },
    },
    (old) => {
      if (!old?.pages) return old;

      let wasRemoved = false;
      const newPages = old.pages.map((page) => {
        const filteredRows = page.rows.filter((ch) => {
          if (String(ch.id) === String(channelId)) {
            wasRemoved = true;
            return false;
          }
          return true;
        });

        return {
          ...page,
          rows: filteredRows,
          count: wasRemoved ? page.count - 1 : page.count,
        };
      });

      return {
        ...old,
        pages: newPages,
      };
    }
  );
};
